import { API_CONFIG, API_ENDPOINTS } from '../helpers/constants';

interface AccessCheckResponse {
  status?: string;
  company_id?: string;
}

interface AccessRequestResponse {
  status?: string;
  message?: string;
  request?: unknown;
}

const DEFAULT_SUBMITTED_MESSAGE = 'Your access request has been sent to your company admin.';

/**
 * Submit a platform SSO access request using the web-safe backend contract.
 *
 * Returns the message to show the user - this is NOT always "request sent":
 * if a prior request for this email was already approved and an Invitation
 * is still sitting unaccepted, the backend returns status="invited" instead
 * of creating a duplicate request, and this surfaces that distinct message
 * rather than silently reporting success as if a new request went out.
 */
export async function requestWebAccess(email: string, accessRequestToken: string): Promise<string> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    throw new Error('We could not determine your SSO email. Please enter your work email and try again.');
  }
  if (!accessRequestToken.trim()) {
    throw new Error('Unable to submit your access request. Please try again.');
  }

  const checkResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.WEB_ACCESS.ACCESS_CHECK}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizedEmail }),
  });
  const checkData = (await checkResponse.json().catch(() => ({}))) as AccessCheckResponse;

  if (!checkResponse.ok || checkData.status !== 'company_found' || !checkData.company_id) {
    throw new Error('Unable to find your organization. Please check your work email or ask your admin.');
  }

  const requestResponse = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.WEB_ACCESS.ACCESS_REQUESTS}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: normalizedEmail,
      company_id: checkData.company_id,
      source: 'web_sso',
      access_request_token: accessRequestToken.trim(),
    }),
  });
  const requestData = (await requestResponse.json().catch(() => ({}))) as AccessRequestResponse;

  const isKnownSuccessStatus = requestData.status === 'pending' || requestData.status === 'invited';
  if (!requestResponse.ok || (!isKnownSuccessStatus && !requestData.request)) {
    throw new Error('Unable to submit your access request. Please try again.');
  }

  return requestData.message || DEFAULT_SUBMITTED_MESSAGE;
}
